import { BuildModuleParamsType } from 'types/api-internal';
import {
  SugarMoneyApiAddressRequestType,
  SugarMoneyApiAvailableCandlestickResolutionType,
  SugarMoneyApiCandlestickDataResponseType,
  SugarMoneyApiCoinResponseType,
  SugarMoneyApiCommentRequestType,
  SugarMoneyApiCommentResponseType,
  SugarMoneyApiPostSignTransactionRequestType,
  SugarMoneyApiPostCoinRequestType,
  SugarMoneyApiCoinCommentResponseType,
  SugarMoneyApiCoinFavoriteResponseType,
  SugarMoneyApiCoinMetadataOperationEnum,
  SugarMoneyApiCoinMintResponseType,
  SugarMoneyApiCoinUnFavoriteResponseType,
  SugarMoneyApiTradesResponseType,
  CoinHoldersResponseType,
  SugarMoneyTransactionStatusResponseType,
} from '../..';
import { uploadMetadata } from './metadata';
import { checkTransactionLoop } from '../../utils/common';

export const coinModule = ({ makeApiRequest, checkTransactionStatus }: BuildModuleParamsType) => ({
  /**
   * Get the `SugarMoney` suffixed coin public key.
   *
   * Note this endpoint is rate limited.
   *
   * @returns The coin public key
   */
  getCoinPublicKey: async () =>
    makeApiRequest<string>({
      endpoint: '/coin/mint',
      options: { method: 'get' },
    }),

  /**
   * Create a new coin. This coin will be created in our backend, not on chain.
   * To create it on chain call the `signTransaction` endpoint.
   *
   * Note this endpoint is rate limited.
   *
   * @param data - The data to create the coin
   * @returns The coin public key
   */
  postCoin: async (data: SugarMoneyApiPostCoinRequestType) => {
    const { data: metadataUploadResponse } = await uploadMetadata({
      file: data.image,
      metadata: data.metadata,
      operation: SugarMoneyApiCoinMetadataOperationEnum.CoinCreationViaApi,
      makeApiRequest,
    });

    if (
      metadataUploadResponse &&
      typeof metadataUploadResponse === 'object' &&
      'ipfsUrlOriginalImage' in metadataUploadResponse
    ) {
      const payload = {
        ipfsUrlOriginalImage: metadataUploadResponse.ipfsUrlOriginalImage,
        ipfsUrlCompressedImage: metadataUploadResponse.ipfsUrlCompressedImage,
        ipfsUrlMetadata: metadataUploadResponse.ipfsUrlMetadata,
        fileId: metadataUploadResponse.fileId,
        migrationInPsol: data.migrationInPsol,
      };

      return makeApiRequest<SugarMoneyApiCoinResponseType>({
        endpoint: '/coin/mint',
        data: payload,
        options: { method: 'post' },
      });
    }

    throw new Error('Failed to upload metadata');
  },

  /**
   * Use this to generate the coin metadata IPFS URL if you want to create a coin directly on chain, not using the API.
   */
  generateMetadataIpfsUrl: async (data: SugarMoneyApiPostCoinRequestType) => {
    const { data: metadataUploadResponse } = await uploadMetadata({
      file: data.image,
      metadata: data.metadata,
      operation: SugarMoneyApiCoinMetadataOperationEnum.IpfsUrlForMetadata,
      makeApiRequest,
    });

    if (metadataUploadResponse && typeof metadataUploadResponse === 'string') {
      return metadataUploadResponse;
    }

    throw new Error('Failed to generate metadata IPFS URL');
  },

  /**
     * Signs a transaction for a first buy. This will create the coin on chain and is
     * required if you want your coin to use a `SugarMoney` suffixed public key.
     *
     * Should only ever be called once per coin. Example usage:
     * 
     * ```
     *  const sugarMoneyProgram = getSdk({ provider });

        const [createIxs] = await sugarMoneyProgram.createIx(name, ticker, ipfsUrl, coinPublicKey);

        const transaction = new Transaction();

        transaction.add(...createIxs);

        const buyIx = await sugarMoneyProgram.getTrade({
          tradeType: TradeTypeEnum.ExactIn,
          amountToTrade: new BN(solAmount * LAMPORTS_PER_SOL),
          tradeDirection: TradeDirectionEnum.Buy,
          mint: new PublicKey(mint),
        });

        transaction.add(buyIx);

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash({
          commitment: 'confirmed',
        });

        transaction.recentBlockhash = blockhash;
        transaction.feePayer = wallet.publicKey!;

        const userSignedTx = await wallet.signTransaction?.(transaction);

        const serializedTransaction = userSignedTx
          .serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          })
          .toString('base64');

        await sugarMoneyApi.coin.postSignTransaction({
          serialisedTx: serializedTransaction,
          mint: coinPublicKey.toString(),
          blockhash,
          lastValidBlockHeight,
        });
      };
     * ```
     *
     * @param data - The data to sign the transaction
     * @returns The transaction signature
     */
  postSignTransaction: async (data: SugarMoneyApiPostSignTransactionRequestType) => {
    const response = await makeApiRequest<SugarMoneyTransactionStatusResponseType>({
      endpoint: '/transaction/sign',
      data,
      options: { method: 'post' },
    });

    return checkTransactionLoop({
      signature: response.signature,
      checkTransactionStatus,
    });
  },

  get: async ({ address }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<SugarMoneyApiCoinResponseType>({
      endpoint: `coin/${address}`,
      options: { method: 'get' },
    }),

  getComments: async ({ address, page, limit }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<SugarMoneyApiCoinCommentResponseType[]>({
      endpoint: `coin/${address}/comments`,
      options: { method: 'get' },
      searchParams: { page, limit },
    }),

  comment: async ({ address, comment }: SugarMoneyApiCommentRequestType) =>
    makeApiRequest<SugarMoneyApiCommentResponseType>({
      endpoint: `coin/${address}/comment`,
      data: { comment },
      options: { method: 'post' },
    }),

  getTrades: async ({ address, page, limit }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<SugarMoneyApiTradesResponseType[]>({
      endpoint: `coin/${address}/trades`,
      options: { method: 'get' },
      searchParams: { page, limit },
    }),

  getHolders: async ({ address }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<CoinHoldersResponseType>({
      endpoint: `coin/${address}/holders`,
      options: { method: 'get' },
    }),

  getMint: async () =>
    makeApiRequest<SugarMoneyApiCoinMintResponseType>({
      endpoint: 'coin/mint',
      options: { method: 'get' },
    }),

  favorite: async ({ address }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<SugarMoneyApiCoinFavoriteResponseType>({
      endpoint: `coin/${address}/favorite`,
      options: { method: 'post' },
    }),

  unfavorite: async ({ address }: SugarMoneyApiAddressRequestType) =>
    makeApiRequest<SugarMoneyApiCoinUnFavoriteResponseType>({
      endpoint: `coin/${address}/unfavorite`,
      options: { method: 'post' },
    }),

  featured: async () =>
    makeApiRequest<SugarMoneyApiCoinResponseType>({
      endpoint: 'coin/featured',
      options: { method: 'get' },
    }),

  /**
   * You can only delete a coin if it is yours and was not created on chain.
   */
  delete: async ({ address }: { address: string }) =>
    makeApiRequest<boolean>({
      endpoint: `coin/${address}/delete`,
      options: { method: 'post' },
    }),

  candlestick: async ({
    address,
    resolution,
    offset,
    limit,
    toTimestamp,
    isFirstDataRequest,
  }: {
    address: string;
    resolution: SugarMoneyApiAvailableCandlestickResolutionType;
    offset: number;
    limit: number;
    toTimestamp: number;
    isFirstDataRequest?: boolean;
  }) =>
    makeApiRequest<SugarMoneyApiCandlestickDataResponseType>({
      endpoint: `coin/${address}/candlestick`,
      options: { method: 'get' },
      searchParams: { resolution, offset, limit, toTimestamp, isFirstDataRequest },
    }),
});
